import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useContext,
  useCallback,
  useMemo,
  useRef
} from 'react';
import * as d3 from 'd3';
import { styled } from '@mui/material/styles';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import MenuIcon from '@mui/icons-material/Menu';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { CONSTANTS } from 'src/components/Constants';
import {
  AppContext,
  UserContext,
  ThemeContext,
  SnackContext
} from 'src/store/ContextStore';
import { EVENTS } from 'src/components/admin/appReducer';
import { usePedigreeInstance } from '../pedigree-tool/pedigree/instanceContext';
import useVAFeatureFlags from 'src/hooks/useVAFeatureFlags';
import {
  getUserSidebarItems,
  SIDEBAR_ITEMS,
  SIDEBAR_VALUE,
  STUDENT_SIDEBAR_ITEMS
} from './util';
import { isAnonymousActive, isAnonymousOrLoading } from '../anonymousPedigrees';
import Tags from './sections/Tags';
import PersonTags from './sections/PersonTags';
import ExternalFamilyIds from 'src/components/proband-info/ExternalFamilyIds';
import FamilyNote from 'src/components/proband-info/FamilyNote';
import PersonMenu from './sections/PersonMenu';
import ReproductiveSection from './sections/ReproductiveSection';
import Suggestions from './sections/Suggestions';
import PedigreeMatchMenu from './sections/PedigreeMatchMenu';
import FormSubmission from './sections/FormSubmission';
import AddRelativePanel from './sections/AddRelativePanel/AddRelativePanel';
import DiagnosesMenu from './sections/diagnoses-menu/DiagnosesMenu';
import GenotypesMenu from './sections/genotypes-menu/GenotypesMenu';
import PhenotypesMenu from './sections/phenotypes-menu/PhenotypesMenu';
import RiskFactorsMenu from './sections/RiskFactorsMenu';
import FamilyHistoryMenu from './sections/FamilyHistoryMenu';
import ExportMenu from './sections/export-menu/ExportMenu';
import EpicEmrButtonWrapper from 'src/components/integrations/epic/EpicEmrButton';
import ClinicalNotes from './sections/clinical-notes-section/ClinicalNotes';
import Pathways from './sections/Pathways';
import RecordStatusMenu from './sections/RecordStatusMenu';
import { APICONSTANTS } from 'src/components/Constants';
import usePersonLinkStatus from 'src/hooks/usePersonLinkStatus';
import axios from 'src/axios';
import { useDirtyState } from 'src/hooks/useDirtyState';
import SaveFooter from './SaveFooter';
import SidebarLockBanner from './SidebarLockBanner';
import TakeEditingLockBanner from './TakeEditingLockBanner';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import KeyboardTabIcon from '@mui/icons-material/KeyboardTab';
import HistoryIcon from '@mui/icons-material/History';
import CustomDialog from 'src/components/UI/dialog/CustomDialog';
import ModificationHistoryModal from './ModificationHistoryModal';
import SidebarSection from './SidebarSection';
import { buildSectionCounts, buildSectionSummaries } from './sectionSummary';
import SectionIndex from './SectionIndex';
import useSectionFacts from './useSectionFacts';
import { buildPersonHeader } from './personHeader';
import PersonHeaderBand from './PersonHeaderBand';
import usePedigreeFieldLabels from 'src/hooks/usePedigreeFieldLabels';
import { BACKGROUND_LOCK_IDLE_RELEASE_MS } from 'src/store/workspace/workspaceConfig';
import { personLockIntent, takeLockIntent } from './personLockPolicy';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';

// TGCLOUD-1669 Phase 5.
//
// The `expand ? '95vw' : 400` branch this replaces is the toggle that was
// removed for looking poor, and it deserved to be: 95vw stretched ONE column to
// the width of the window, so a 22-field form stayed 22 rows with 1,000px of
// empty paper beside each one. Nothing got denser and the pedigree disappeared.
//
// Promoting now RE-LAYS OUT rather than stretching, and it stops well short of
// the window: `calc(100vw - 260px)` leaves a strip of canvas so the drawer still
// reads as floating over a family rather than having replaced it. 1080px is the
// cap because past it the three columns stop earning their width.
// The person-tab region's shell (TGCLOUD-1669 Phase 7). Named and exported so
// the one property that matters is pinned by a test rather than only by a
// browser.
//
// IN FLOW WITH A DEFINITE HEIGHT, and both halves are load-bearing.
// `position: absolute; inset: 0` reads as the obvious choice and renders
// NOTHING: ChartBox's root is `height: 100%` inside PedigreeBox's Container,
// which sets width and position but no height, so the 100% resolves against an
// auto-height parent and ChartBox takes its height from its content. On a
// pedigree tab that content is the canvas. On a person tab the canvas is
// suppressed, so ChartBox had no in-flow child at all, collapsed to zero, and
// an absolutely positioned child of a zero-height box is invisible.
//
// Being in flow also hands ChartBox its height back, so anything else rendered
// in there has a real box to position against.
//
// 65px is the app bar, the same offset the Drawer gives its paper.
export const REGION_SHELL_SX = {
  height: 'calc(100vh - 65px)',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  bgcolor: 'background.paper',
  overflow: 'hidden'
};

// Whether the sidebar's editable sections are read-only, exported and pure so
// the rule can be asserted without mounting this component (the H8 remediation).
//
// THE DEFECT IT ENCODES. The `pedigreeLockedByOther` clause used to be
// `(!hasPersonLock && pedigreeLockedByOther)`: holding the PERSON lock made the
// sections editable while a colleague held the FAMILY lock, on the principle
// that a person lock governs that patient's own fields. The principle is sound;
// the write path for it does not exist. Every section here "saves" by mutating
// `memberRecords`, and the only thing that writes member records to the server —
// PedigreeChart's debounced autosave — refuses to POST while another user holds
// the family lock, because the endpoint takes the WHOLE member array and would
// overwrite theirs. So the edit was accepted, PersonMenu snacked "Saved", and
// nothing left the browser.
//
// A person tab reached that state with no user action at all: it acquires the
// person lock as it opens. Until a per-person write exists (the member-details
// endpoint is the obvious candidate), holding a person lock must NOT unlock a
// family-locked pedigree.
//
// SCOPE. This gate applies ONLY to sections whose save is a mutation of
// `memberRecords` — PersonMenu, phenotypes, genotypes, diagnoses, reproductive,
// Add Relative. It is the family lock's reach over the ONE endpoint that
// replaces the whole member array. Sections with their own endpoints are gated
// by clinicalRecordSectionsAreDisabled below; a blanket gate took away work that
// has always saved correctly under a foreign family lock.
export const sectionsAreDisabled = ({
  isSecondaryRecord,
  personLockedByOther,
  pedigreeLockedByOther,
  bulkEditActive
}) =>
  !!isSecondaryRecord ||
  !!personLockedByOther ||
  !!pedigreeLockedByOther ||
  !!bulkEditActive;

// The narrower gate, for Clinical Records.
//
// THE DEFECT IT FIXES. Suspending the person-lock override (above) disabled
// Clinical Records along with everything else. Clinical records do NOT go
// through memberRecords: ClinicalNotes POSTs to /clinical-notes itself, per
// record, and that write is authorised per note — it landed perfectly well in
// 7.1.0 while a colleague held the family lock, and there is no member array to
// overwrite. So `pedigreeLockedByOther` is deliberately NOT an input here: a
// family lock is about the pedigree document, and blocking a consultation note
// because someone else has the chart open removes working clinical
// functionality to fix a bug that never touched it.
//
// The three reasons that DO still apply are the ones about this record rather
// than about the chart: someone else holds THIS person's lock, this is a
// secondary (linked) record whose notes live on the primary, or a bulk edit is
// in progress over the selection.
export const clinicalRecordSectionsAreDisabled = ({
  isSecondaryRecord,
  personLockedByOther,
  bulkEditActive
}) => !!isSecondaryRecord || !!personLockedByOther || !!bulkEditActive;

const PROMOTED_WIDTH = 'min(1080px, calc(100vw - 260px))';

const StyledList = styled('div')(({ promoted, full }) => ({
  width: full ? 'auto' : promoted ? PROMOTED_WIDTH : 400,
  transition: 'width .16s cubic-bezier(.4,0,.2,1)',
  display: 'flex',
  flexDirection: 'column',
  height: '100%'
}));

const BtnContainer = styled('div')({
  position: 'absolute',
  right: 0,
  top: 0,
  zIndex: 10
});

const ListContainer = styled(List)({
  overflowX: 'hidden'
});

const ScrollableContent = styled('div')({
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden'
});

// A quiet header control. Deliberately not an icon button: the two things it
// launches are easy to confuse, and an icon alone is what made both of them
// unfindable in the first place. The count is on the record browser only,
// because a change history has no count a clinician acts on.
function HeaderActionButton({ icon, count, onClick, children }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        height: 26,
        px: 1,
        border: '1px solid #c9d2da',
        borderRadius: '4px',
        bgcolor: '#fff',
        color: '#46525f',
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
        '&:hover': {
          bgcolor: '#f4f8fa',
          borderColor: '#0e7186',
          color: '#0e7186'
        }
      }}
    >
      {icon}
      {children}
      {count != null && (
        <Box
          component="span"
          sx={{ fontWeight: 800, color: '#0e7186', ml: 0.25 }}
        >
          {count}
        </Box>
      )}
    </Box>
  );
}

const modalTitleMap = {
  [CONSTANTS.auditLogType.clinicalRecord]: 'Clinical History',
  [CONSTANTS.auditLogType.riskFactors]: 'Risk History',
  [CONSTANTS.auditLogType.phenotypes]: 'Phenotypes History',
  [CONSTANTS.auditLogType.genotypes]: 'Genotypes History',
  [CONSTANTS.auditLogType.diagnoses]: 'Disease History',
  [CONSTANTS.auditLogType.personDetails]: 'Person History',
  // TGCLOUD-1669 — every section's changes for this person in one list. Called
  // "Change history", never "History" on its own: the record BROWSER answers
  // "what has happened to this patient", this answers "who edited the record".
  // They are different questions for different people and must not read alike.
  [CONSTANTS.auditLogType.personAll]: 'Change History'
};

// The sidebar accordion row (styled Accordion + summary/details) lives in
// SidebarSection, shared with the legacy patient sidebar and gated by the
// PEDIGREE_SIDEBAR_REFRESH flag.

export default function PedigreeSideBar({
  data,
  sidebar,
  setSidebar,
  setData,
  memberRecords,
  setMemberRecords,
  // labelsSet is no longer read here: the label and legend configuration it fed
  // moved to the pedigree panel (TGCLOUD-1674). setLabelsSet stays — the
  // diagnoses/phenotypes/genotypes sections still write per-record visibility.
  setLabelsSet,
  setPedigreeState,
  personLockHook,
  bulkEditActive,
  pedigreeLockedByOther,
  analysis,
  onLinkStatusChanged,
  isSaving = false,
  // Called with 'self' | 'other' | 'none' whenever this tab's hold on the
  // PERSON lock changes, so the tab strip can show the truth about the patient
  // rather than about their family. See the note at the effect below.
  onPersonLockStateChange,
  // True while a person tab is still resolving its member. See the note at the
  // render site below.
  personPending = false,
  // The workspace actions the header's two tab controls need. Props rather
  // than a context subscription: see the note beside `workspace` below.
  openPedigree,
  openPerson,
  // TGCLOUD-1669 Phase 7. 'drawer' is the pedigree tab's right-anchored
  // persistent Drawer. 'region' is the person tab: the SAME content, rendered
  // in flow at the width of its parent, with no Drawer around it and nothing
  // to promote or demote because it is already as wide as it gets.
  //
  // The content is shared rather than duplicated, so a section can never render
  // one way here and another way there.
  variant = 'drawer'
}) {
  const isRegion = variant === 'region';
  // The two workspace actions arrive as PROPS, not from useWorkspace().
  //
  // Subscribing here cost a tab switch far more than it looked. Context
  // propagation bypasses parent memo bailouts, and the workspace mints a fresh
  // state object on every activation, so a single consumer in this component
  // re-rendered the sidebar and all seventeen always-mounted sections in EVERY
  // open tab, not just the two involved in the switch. ChartBox already
  // subscribes and already memoises this element, so taking them as props puts
  // that bailout back. Both are stable useCallbacks, so they cost nothing as
  // dependencies.
  //
  // Undefined outside the multi-tab workspace (flag off, or an FHQ preview),
  // which is what keeps "Open in tab" out of a single-chart view with no strip
  // to open into.
  const workspace = React.useMemo(
    () => (openPedigree || openPerson ? { openPedigree, openPerson } : null),
    [openPedigree, openPerson]
  );
  const { theme } = useContext(ThemeContext);
  const { setSnack } = useContext(SnackContext);
  const { content, dispatchUpdate } = useContext(AppContext);
  // Scope canvas node lookups to this chart (multi-pedigree-tabs); document
  // fallback keeps a lone chart unchanged.
  // `isActive` is already on this context and defaults true outside a
  // provider, so reading it costs no new subscription and cannot change
  // single-chart behaviour.
  const { containerRef, isActive } = usePedigreeInstance();
  const {
    pedigreeSelectedUsers: users,
    settings: { epicEmrApi, pedigreeSetting, systemFeatures }
  } = content;
  // Admin-controlled feature flags. `null` while the initial fetch is in
  // flight; we treat undecided as "leave items visible" to avoid a flicker
  // where a feature briefly disappears on first paint.
  const featureFlags = useVAFeatureFlags();
  const { selectedUser } = users;
  // TWO helpers, deliberately opposite while `pedigreeSetting` is still loading
  // (see ../anonymousPedigrees.js), and this panel needs both:
  //
  //  - `anonymousActive` fails OPEN. It decides which SECTIONS are listed, and
  //    hiding normal-deployment UI on first paint is the worse trade there.
  //  - `anonymousOrLoading` fails CLOSED. It is what any PII may be gated on.
  //
  // TGCLOUD-1768: the header was reading the fail-open one, so an anonymous
  // deployment rendered the patient's NAME in the window before
  // GET /settings/pedigree resolved — while PersonMenu, directly beneath it,
  // correctly hid the same name in the form. The header degrades to the member
  // id while loading, which `resolveSidebarHeader` already does when a person
  // has no name recorded.
  const anonymousActive = isAnonymousActive(pedigreeSetting);
  const anonymousOrLoading = isAnonymousOrLoading(pedigreeSetting);
  const { user: loggedInUser, setUser } = useContext(UserContext);

  const [openModal, setOpenModal] = React.useState(false);
  const [modificationType, setModificationType] = useState(false);
  const [modificationSubType, setModificationSubType] = useState({});
  // Optional: scope the history modal to a single record (per-note history).
  const [modificationRecordId, setModificationRecordId] = useState(null);
  const accordionRefs = useRef({});
  // Declared beside accordionRefs because the promoted view's scroll spy reads
  // both, and it is defined above them.
  const sidebarRef = useRef();

  // Dirty-state tracking for Diseases / Phenotypes / Genotypes
  const {
    markDirty,
    markClean,
    markAllClean,
    isDirty,
    dirtyCount,
    dirtySectionLabels
  } = useDirtyState();

  // Refs so PedigreeSideBar can trigger each section's save / discard
  const diagnosesSaveRef = useRef(null);
  const phenotypesSaveRef = useRef(null);
  const genotypesSaveRef = useRef(null);
  const personSaveRef = useRef(null);
  const tagsSaveRef = useRef(null);
  const externalIdsSaveRef = useRef(null);
  const familyNoteSaveRef = useRef(null);
  const diagnosesDiscardRef = useRef(null);
  const phenotypesDiscardRef = useRef(null);
  const genotypesDiscardRef = useRef(null);
  const personDiscardRef = useRef(null);
  const tagsDiscardRef = useRef(null);
  const externalIdsDiscardRef = useRef(null);
  const familyNoteDiscardRef = useRef(null);

  // PERSON LOCK LIFECYCLE (TGCLOUD-1669 Phase 8).
  //
  // The standing rule is at most one edit lock in normal use, and the tab strip
  // has to be a truthful map of who holds what. Three rules, in order of
  // precedence:
  //
  //   1. the ACTIVE tab holds the lock
  //   2. a background tab with UNSAVED work KEEPS it, because releasing a lock
  //      out from under an unsaved edit is how you lose the edit
  //   3. otherwise a background tab releases after the idle window
  //
  // Before this, the effect was keyed on the selected pid alone and read
  // nothing about activation, so every open person tab heartbeated a real lock
  // for as long as it stayed open. Colleagues were locked out of records nobody
  // was looking at.
  //
  // The idle release POSTS. Letting the 90s TTL reap the row instead would be
  // silent: only the release route emits a socket event, so every other client
  // would go on showing this user as the holder until it rejoined the room.
  const hasUnsavedWork = dirtyCount > 0;

  // Whether THIS mount currently holds it. The hook's counter knows how many
  // mounts want the lock; this knows whether we are one of them, so a release
  // cannot be issued twice or by a mount that never acquired.
  const holdingPidRef = useRef(null);
  // Mirrored as state because the badge has to re-render when it changes; the
  // ref is what the synchronous release paths read.
  const [holdsPersonLock, setHoldsPersonLock] = useState(false);
  const heartbeatIntervalRef = useRef(null);

  const releaseHeldLock = useCallback(() => {
    const held = holdingPidRef.current;
    if (held == null || !personLockHook) return null;
    holdingPidRef.current = null;
    setHoldsPersonLock(false);
    personLockHook.releaseLock(held);
    return held;
  }, [personLockHook]);

  const lockIntent = personLockIntent({
    isActive,
    hasUnsavedWork,
    isHolding: holdingPidRef.current != null
  });

  useEffect(() => {
    const pid = data?.pid;
    if (!pid || !personLockHook || lockIntent !== 'hold') return undefined;

    if (holdingPidRef.current !== pid) {
      // The ref goes up FIRST because it is the in-flight marker that stops a
      // re-render firing a second acquire. `holdsPersonLock` does not: it is
      // what the tab strip reads to draw the pencil and what the sections read
      // to decide they are editable, so it may only go true once the server has
      // actually said yes. Setting it optimistically meant a clinician who lost
      // the race to a colleague was shown as the holder and given editable
      // fields against a lock they did not have.
      holdingPidRef.current = pid;
      // Promise.resolve rather than a bare .then: an acquireLock that returned
      // void would otherwise throw a TypeError inside this effect, which in a
      // render path that has already blanked this tab once is not a trade worth
      // taking for one fewer call.
      Promise.resolve(personLockHook.acquireLock(pid)).then((res) => {
        // A different person may have been selected while this was in flight.
        if (holdingPidRef.current !== pid) return;
        if (res?.success) {
          setHoldsPersonLock(true);
          return;
        }
        // Not ours. Drop the marker too, so the idle timer cannot later fire a
        // release for a lock this tab never took.
        holdingPidRef.current = null;
        setHoldsPersonLock(false);

        // A CONFLICT explains itself: usePersonLocks has already stored the
        // other user's row, so SidebarLockBanner names the holder and offers
        // Request access. Any OTHER failure has nothing to render, and being
        // honest about not holding the lock would then leave the tab read-only
        // with no banner and no reason - a silent dead end, and exactly the
        // shape of the Phase 7 defect where the person tab showed nothing and
        // only the browser console said the acquire had failed. TGCLOUD-1774
        // makes this a live path rather than a theoretical one: the legacy
        // personId_1 unique index turns a second person lock into a 500.
        //
        // Named by familyMemberId, never by name: on an anonymous deployment
        // a snackbar carrying the patient's name is a PII leak.
        if (!res?.conflict) {
          setSnack({
            status: true,
            severity: 'warning',
            msg: `Could not take the editing lock for ${
              data?.familyMemberId || 'this patient'
            }. The record is read-only. Close and reopen the tab to try again.`
          });
        }
      });
    }
    heartbeatIntervalRef.current = setInterval(() => {
      personLockHook.sendHeartbeat(pid);
    }, 30000);

    // Only the heartbeat stops here. Releasing is the idle timer's job, or the
    // unmount effect's; doing it in this cleanup would release the moment the
    // tab lost focus, which is the opposite of the idle window.
    return () => clearInterval(heartbeatIntervalRef.current);
  }, [data?.pid, lockIntent]); // eslint-disable-line react-hooks/exhaustive-deps

  // PER TAB, deliberately. One shared timer would mean only the most recently
  // backgrounded tab ever released, so three tabs could show a pencil at once
  // and the strip would lie. This effect lives in the mount, so there is one
  // timer per tab by construction.
  useEffect(() => {
    const pid = data?.pid;
    if (!pid || !personLockHook || lockIntent !== 'release-after-idle')
      return undefined;
    if (holdingPidRef.current !== pid) return undefined;

    const timer = setTimeout(() => {
      const released = releaseHeldLock();
      if (released != null) {
        setSnack({
          status: true,
          severity: 'info',
          msg: `Editing lock released for ${
            data?.familyMemberId || 'this patient'
          } while the tab was in the background.`
        });
      }
    }, BACKGROUND_LOCK_IDLE_RELEASE_MS);
    return () => clearTimeout(timer);
  }, [data?.pid, lockIntent]); // eslint-disable-line react-hooks/exhaustive-deps

  // The exception, said out loud. A clinician who leaves a tab with unsaved
  // work should know why their colleague still cannot edit it.
  const toldAboutRetentionRef = useRef(false);
  useEffect(() => {
    if (isActive || !hasUnsavedWork || holdingPidRef.current == null) {
      toldAboutRetentionRef.current = false;
      return;
    }
    if (toldAboutRetentionRef.current) return;
    toldAboutRetentionRef.current = true;
    setSnack({
      status: true,
      severity: 'warning',
      msg: `Keeping the editing lock for ${
        data?.familyMemberId || 'this patient'
      }: there are unsaved changes in this tab.`
    });
  }, [isActive, hasUnsavedWork]); // eslint-disable-line react-hooks/exhaustive-deps

  // Deselect, switch person, or close the tab.
  useEffect(() => {
    return () => {
      clearInterval(heartbeatIntervalRef.current);
      releaseHeldLock();
    };
  }, [data?.pid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Release lock on tab close. The URL is built by the hook (TGCLOUD-1767): it
  // is the only thing that knows the family the lock is scoped to, and it reads
  // the axios base URL rather than hardcoding '/api', which used to 404 the
  // release wherever PUBLIC_URL is non-empty.
  useEffect(() => {
    if (!data?.pid || !personLockHook) return;
    const pid = data.pid;
    const handleUnload = () => {
      personLockHook.releaseBeacon(pid);
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [data?.pid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compute whether current person is locked by someone else
  const lockedByOther =
    data?.pid && personLockHook
      ? personLockHook.isLockedByOther(data.pid)
      : false;

  // `getLock`, not `lockedPersons.get` — `data.pid` is a Number and the map is
  // keyed on the String the server sends (TGCLOUD-1767).
  const lockInfo = lockedByOther ? personLockHook.getLock(data.pid) : null;

  // Current user holds the person lock for the selected node
  const hasPersonLock =
    data?.pid && personLockHook ? personLockHook.isMine(data.pid) : false;

  // THE STRIP MUST TELL THE TRUTH. Its badge had exactly one writer, and that
  // writer reflects the FAMILY lock, so a person tab showed a pencil or an eye
  // for the pedigree rather than for the patient it is about. A person tab now
  // reports its own state and the workspace prefers it (TGCLOUD-1669 Phase 8).
  useEffect(() => {
    if (!onPersonLockStateChange) return;
    onPersonLockStateChange(
      holdsPersonLock ? 'self' : lockedByOther ? 'other' : 'none'
    );
  }, [holdsPersonLock, lockedByOther, onPersonLockStateChange]);

  let familyMemberId = useMemo(() => {
    return data?.proBandId ?? data?.familyMemberId;
  }, [data]);

  const { status: personLinkStatus, linkData: personLinkData } =
    usePersonLinkStatus(data ? familyMemberId : null);
  const isSecondaryRecord = personLinkStatus === 'secondary';
  // Sections are disabled when the person is locked by someone else, when the
  // PEDIGREE is locked by another user, when this is a secondary (linked)
  // record, or during a bulk edit. The rule — and why holding a person lock no
  // longer unlocks a family-locked pedigree — is in sectionsAreDisabled above.
  const sectionDisabled = sectionsAreDisabled({
    isSecondaryRecord,
    personLockedByOther: lockedByOther,
    pedigreeLockedByOther,
    bulkEditActive
  });
  // Clinical Records writes through its own per-record endpoints, so the family
  // lock does not reach it. See clinicalRecordSectionsAreDisabled above.
  const clinicalRecordsDisabled = clinicalRecordSectionsAreDisabled({
    isSecondaryRecord,
    personLockedByOther: lockedByOther,
    bulkEditActive
  });

  // TGCLOUD-1669 Phase 8 - the inline way back in. The rule lives in
  // personLockPolicy so the four exclusions are pinned by tests rather than
  // being a boolean expression nobody can exercise.
  //
  // SUSPENDED, for the reason in sectionsAreDisabled. takeLockIntent only ever
  // returns 'offer' when the family is locked by another user, which is exactly
  // the state in which taking the person lock now buys nothing: the sections
  // stay disabled because the save cannot be sent. Offering a button that
  // silently changes nothing is worse than the blank state Phase 8 set out to
  // fix — and the state is no longer blank, because LockBanner's "being edited
  // by X / Request edit access" banner renders on a person tab now.
  //
  // The gate is deliberately HERE and not in personLockPolicy: the policy and
  // its tests still describe the behaviour we want back the moment a per-person
  // write path exists, and this is the single call site that has to change to
  // re-enable it.
  const PERSON_LOCK_OVERRIDES_FAMILY_LOCK = false;
  const [takingLock, setTakingLock] = useState(false);
  const offerTakeLock =
    PERSON_LOCK_OVERRIDES_FAMILY_LOCK &&
    !!data?.pid &&
    !!personLockHook &&
    takeLockIntent({
      lockedByOther,
      hasPersonLock,
      pedigreeLockedByOther: !!pedigreeLockedByOther,
      isSecondaryRecord,
      bulkEditActive: !!bulkEditActive
    }) === 'offer';

  const handleTakeLock = useCallback(async () => {
    const pid = data?.pid;
    if (!pid || !personLockHook) return;
    setTakingLock(true);
    try {
      const res = await personLockHook.acquireLock(pid);
      if (res?.success) {
        // No snackbar on success. The banner disappears and the fields become
        // editable, which is the whole confirmation a clinician needs; a toast
        // on top of that is noise on the happy path.
        holdingPidRef.current = pid;
        setHoldsPersonLock(true);
        return;
      }
      // Lost the race. A conflict populates lockedPersons, so SidebarLockBanner
      // takes over on the next render and names whoever got there first, which
      // is a better answer than anything this could say.
      if (!res?.conflict) {
        setSnack({
          status: true,
          severity: 'warning',
          msg: `Could not take the editing lock for ${
            data?.familyMemberId || 'this patient'
          }. The record stays read-only.`
        });
      }
    } finally {
      setTakingLock(false);
    }
  }, [data?.pid, data?.familyMemberId, personLockHook, setSnack]);

  // TGCLOUD-1669 Phase 3 — bumped by the header's Clinical records control.
  // ClinicalNotes owns the browser (it holds the records), and it is mounted
  // whatever the accordion state, so the header can open it without the records
  // moving or being fetched a second time.
  // TGCLOUD-1669 Phase 5 — the peek/promoted state.
  //
  // Deliberately NOT reset when a different person is selected. Concept C
  // proposed resetting on canvas selection, on the grounds that clicking the
  // canvas is a family action; but once promoted the canvas is mostly covered,
  // so in practice the reset would only fire when someone had already chosen to
  // work patient-by-patient, and collapsing under them would read as a bug.
  const [promoted, setPromoted] = useState(variant === 'region');
  // Column three is the record browser, docked. It is rendered by ClinicalNotes
  // through a portal into this node rather than by copying the records up here:
  // ClinicalNotes owns them, is mounted whatever the accordion state, and a
  // second copy in this component's state would be one more thing to keep in
  // step with the first.
  const [dockNode, setDockNode] = useState(null);
  const [dockCollapsed, setDockCollapsed] = useState(false);
  // Collapsing the record column is a DRAWER affordance. There it hands 348px
  // back to the sections, which is worth having. In the person tab the centre
  // column is capped, so collapsing the records widens nothing and just leaves
  // the space empty, which is why the control is not offered there and the
  // state is ignored if it somehow got set.
  const recordsCollapsed = !isRegion && dockCollapsed;
  // Which section the index highlights. Set by a click, and by a scroll spy
  // while the user scrolls the middle column instead.
  const [activeSection, setActiveSection] = useState(null);

  const [browserSignal, setBrowserSignal] = useState(0);

  const [recordStatusRefreshKey, setRecordStatusRefreshKey] = useState(0);
  const [familyHistoryRefreshKey, setFamilyHistoryRefreshKey] = useState(0);

  // ---------------------------------------------------------------------
  // TGCLOUD-1669 Phase 1 — collapsed-section summary lines
  // ---------------------------------------------------------------------
  // Bind the summaries to the memberRecords ROW, not to `data`. `data` is a
  // click-time snapshot built once per node click, and the only effect that
  // resyncs it (ChartBox "Sync data state with latest memberRecords") compares
  // a seven-field STRUCTURAL allowlist — childrenIds, spouseIds, siblingIds,
  // fatherId, motherId, spouseRelationships, dob. diagnoses, phenotypes and
  // genotypes are not in it, so a disease added in the panel never reaches
  // `data` and a `data`-bound count would sit stale until the node was
  // re-clicked. `data` stays as the fallback so the row still renders before
  // memberRecords hydrates.
  const summaryMember = useMemo(() => {
    if (!data) return null;
    const row = (memberRecords || []).find((m) => m.pid === data.pid);
    return row ? { ...data, ...row } : data;
  }, [data, memberRecords]);

  // Five sections fetch on mount but keep the answer in their own useState,
  // where this row cannot read it. They report it up through here. The hook
  // owns the two things that are easy to get wrong: the equality guard that
  // stops a re-report looping, and the per-person reset, which must happen
  // during render rather than in an effect (see useSectionFacts).
  const [sectionFacts, reportSectionFacts] = useSectionFacts(data?.pid);

  // Scroll spy for the index. Only while promoted: in the peek there is no
  // index to highlight, and binding a scroll listener there would be work done
  // for nothing on the surface people use most.
  useEffect(() => {
    const el = sidebarRef.current;
    if (!promoted || !el) return undefined;
    let queued = false;
    const onScroll = () => {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(() => {
        queued = false;
        const top = el.getBoundingClientRect().top + 8;
        let best = null;
        Object.entries(accordionRefs.current).forEach(([key, node]) => {
          if (node && node.getBoundingClientRect().top <= top) best = key;
        });
        setActiveSection((prev) => (best && best !== prev ? best : prev));
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener('scroll', onScroll);
  }, [promoted]);

  // Keeping your place across a layout change.
  //
  // The scroller itself now stays mounted across promote and demote (see the
  // note on the layout below), so scrollTop is no longer reset outright. It is
  // still the wrong thing to preserve: promoting takes the content column from
  // 400px to 464px or 812px, and collapsing the record dock takes it from 464
  // to 812, so every section reflows and the same pixel offset lands somewhere
  // else. A three column field grid is roughly a third the height of the one
  // column it came from.
  // What is anchored instead is the SECTION you were reading: the last section
  // head at or above the top of the scroller, and how far above it sat. After
  // the new layout commits, that same head is put back at that same offset.
  // Anchoring to content rather than to pixels is what makes it survive the
  // reflow, which is the whole reason the naive version did not work.
  const pendingAnchorRef = useRef(null);

  const captureScrollAnchor = React.useCallback(() => {
    const el = sidebarRef.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top;
    let best = null;
    let first = null;
    Object.entries(accordionRefs.current).forEach(([key, node]) => {
      if (!node) return;
      const offset = node.getBoundingClientRect().top - top;
      if (!first || offset < first.offset) first = { key, offset };
      // The closest head that is at or above the fold. 8px of slack so a head
      // resting flush against the top still counts as the one being read.
      if (offset <= 8 && (!best || offset > best.offset))
        best = { key, offset };
    });
    // Scrolled above the first head (or nothing measurable): anchor to the
    // first section so the restore is still a no-op rather than a jump.
    pendingAnchorRef.current = best || first;
  }, []);

  useLayoutEffect(() => {
    const anchor = pendingAnchorRef.current;
    pendingAnchorRef.current = null;
    if (!anchor) return;
    const el = sidebarRef.current;
    const node = accordionRefs.current[anchor.key];
    if (!el || !node) return;
    const delta =
      node.getBoundingClientRect().top - el.getBoundingClientRect().top;
    el.scrollTop += delta - anchor.offset;
  }, [promoted, recordsCollapsed]);

  const sectionCounts = useMemo(
    () => buildSectionCounts({ member: summaryMember, facts: sectionFacts }),
    [summaryMember, sectionFacts]
  );

  // Clicking the index expands the section and scrolls to it. Expanding rather
  // than only scrolling is deliberate: an index entry that scrolls to a
  // collapsed row shows the user the one thing they already had, its label.
  const handleIndexSelect = React.useCallback(
    (value) => {
      setActiveSection(value);
      setSidebar((pre) => ({
        ...pre,
        expandAccordian: pre.expandAccordian?.includes(value)
          ? pre.expandAccordian
          : [...(pre.expandAccordian || []), value],
        scrollTo: value
      }));
    },
    [setSidebar]
  );

  const sectionSummaries = useMemo(
    () =>
      buildSectionSummaries({
        member: summaryMember,
        analysis,
        personLinkStatus,
        anonymous: anonymousOrLoading,
        facts: sectionFacts
      }),
    [
      summaryMember,
      analysis,
      personLinkStatus,
      anonymousOrLoading,
      sectionFacts
    ]
  );

  // External family IDs are a FAMILY-ROOT field that two other places read straight off
  // `pedigreeSelectedUsers.selectedUser`: the chart header (headline / "Master ID" / "Also known as"
  // chips) and the CONFIGURATIONS panel's Display label section. `ExternalFamilyIds` only ever
  // updated its own local state after a save, so neither noticed until the family was reloaded —
  // which is the "did not appear until the pedigree was refreshed" report (TGCLOUD-1666).
  //
  // Write the SERVER's array back, not the local input: the route trims each entry and drops blanks.
  //
  // Deliberately SET_PEDIGREE_SELECTED_USER, not UPDATE_PEDIGREE_SELECTED_USER — the latter
  // discriminates root-vs-member on `value.email` being truthy, so a root without an email silently
  // routes into the family-member branch. Spreading the whole existing `users` slice is what keeps
  // diagnoses/phenotypes/genotypes intact: the reducer replaces the slice wholesale and only
  // recomputes those three when they are absent (and needs `family` present to do it at all).
  const handleExternalIdsSaved = (ids) => {
    if (!selectedUser?._id) return;
    dispatchUpdate({
      type: EVENTS.SET_PEDIGREE_SELECTED_USER,
      value: {
        ...users,
        selectedUser: { ...selectedUser, externalFamilyIds: ids }
      }
    });
  };

  // TGCLOUD-1791 — same shape and the same reason as handleExternalIdsSaved
  // above: the family note is now rendered outside this section (the chart
  // header and the export), so a save has to reach app state or those keep
  // showing the pre-edit text until the family is reloaded.
  const handleFamilyNoteSaved = (familyNote) => {
    if (!selectedUser?._id) return;
    dispatchUpdate({
      type: EVENTS.SET_PEDIGREE_SELECTED_USER,
      value: {
        ...users,
        selectedUser: { ...selectedUser, familyNote }
      }
    });
  };

  const handleSaveAll = async () => {
    if (isDirty('person')) await personSaveRef.current?.();
    if (isDirty('diagnoses')) await diagnosesSaveRef.current?.();
    if (isDirty('phenotypes')) await phenotypesSaveRef.current?.();
    if (isDirty('genotypes')) await genotypesSaveRef.current?.();
    if (isDirty('tags')) await tagsSaveRef.current?.();
    if (isDirty('externalFamilyIds')) await externalIdsSaveRef.current?.();
    if (isDirty('familyNote')) await familyNoteSaveRef.current?.();
    setRecordStatusRefreshKey((k) => k + 1);
  };

  const handleDiscardAll = () => {
    personDiscardRef.current?.();
    diagnosesDiscardRef.current?.();
    phenotypesDiscardRef.current?.();
    genotypesDiscardRef.current?.();
    tagsDiscardRef.current?.();
    externalIdsDiscardRef.current?.();
    familyNoteDiscardRef.current?.();
    markAllClean();
  };

  const handleOpenModel = () => setOpenModal(true);
  const handleCloseModel = () => setOpenModal(false);
  const canAccessExportSettings = [
    CONSTANTS.userTypes.ADMIN,
    CONSTANTS.userTypes.GENETICS,
    CONSTANTS.userTypes.GENETICS_SERVICE_ADMIN
  ].includes(loggedInUser?.userType);

  useEffect(() => {
    if (!loggedInUser || !canAccessExportSettings) return;

    axios.get(APICONSTANTS.getExportSettings).then((res) => {
      const canExport = res.data.canExport ?? false;
      setUser((prev) => {
        // prev.userGroup is the bare ObjectId from the JWT until something
        // promotes it to an object. Spreading a string loses the _id and
        // breaks any reader that needs it (e.g. the User Group chip), so
        // hoist the id into the object form first.
        const existingGroup =
          prev?.userGroup && typeof prev.userGroup === 'object'
            ? prev.userGroup
            : prev?.userGroup
            ? { _id: prev.userGroup }
            : {};
        return {
          ...prev,
          userGroup: { ...existingGroup, canExport }
        };
      });
    });
  }, []);

  let isStudent = useMemo(() => {
    return loggedInUser.userType === CONSTANTS.userTypes.STUDENT;
  }, [loggedInUser]);

  const modificationHistoryHandle = (type, subType = {}, recordId = null) => {
    setModificationType(type);
    setModificationSubType(subType);
    setModificationRecordId(recordId);
    handleOpenModel();
  };

  const unHighlightNodeByPid = (pid) => {
    // Real member pids are positive numbers (proband is 1); freshly-added,
    // unsaved members carry a UUID string. A non-positive numeric pid (e.g. the
    // -1 sentinel seen when closing the sidebar for a synthetic/transient node)
    // has no rendered <g>, so there is nothing to unhighlight — bail quietly
    // rather than log a spurious console.error on every such close.
    if (pid == null || (typeof pid === 'number' && pid < 1)) return;

    const parentGroup = d3
      .select(containerRef.current || document)
      .selectAll('g')
      .filter(function (d) {
        return d?.data?.pid === pid;
      });

    if (!parentGroup.empty()) {
      const targetPath = parentGroup.select('path.te');
      if (!targetPath.empty()) {
        targetPath
          .attr('stroke-width', 10)
          .style('stroke', theme.appearance.text);
      } else {
        console.error('No <path class="te"> found for pid:', pid);
      }
    } else {
      console.error('No <g> element found for pid:', pid);
    }
  };

  const handleClose = () => {
    setSidebar({
      open: false,
      expand: false,
      expandAccordian: []
    });

    if (data) {
      unHighlightNodeByPid(data?.canvasPid ?? data?.pid);
    }
    setData(null);
  };

  const toggleSideBar = (open) => (event) => {
    if (
      event.type === 'keydown' &&
      (event.key === 'Tab' || event.key === 'Shift')
    ) {
      return;
    }

    if (!open) {
      handleClose();
    } else {
      // LEGEND used to be the default-expanded panel; it is now a modal off the
      // pedigree panel (TGCLOUD-1674), so open on the family's own information
      // rather than on nothing.
      const defaultExpandedAccordion = [SIDEBAR_VALUE.tags];

      // Fetch state of expanded accordion from local storage
      const savedAccordionState = localStorage.getItem('pedigreeSidebarState');
      const expandedAccordion = savedAccordionState
        ? JSON.parse(savedAccordionState)
        : null;

      // Determine the final accordion state
      const expandAccordian =
        expandedAccordion === null
          ? defaultExpandedAccordion
          : expandedAccordion;

      setSidebar((pre) => ({ ...pre, open: open, expandAccordian }));
    }
  };

  const handleChangeAccordian = (panel) => (event, newExpanded) => {
    if (newExpanded) localStorage.setItem('sidebar-panel', panel);
    else localStorage.setItem('sidebar-panel', null);
    setSidebar((pre) => {
      const expandAccordian = newExpanded
        ? [...pre.expandAccordian, panel]
        : [...pre.expandAccordian].filter((item) => item !== panel);

      // Save state of accordion to local storage of browser
      localStorage.setItem(
        'pedigreeSidebarState',
        JSON.stringify(expandAccordian)
      );

      return { ...pre, expandAccordian };
    });

    requestAnimationFrame(() => {
      setTimeout(() => {
        if (newExpanded && accordionRefs.current[panel]) {
          accordionRefs.current[panel].scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        } else if (sidebarRef.current) {
          sidebarRef.current.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
      }, 200);
    });
  };

  useEffect(() => {
    const panel = sidebar.scrollTo;
    if (!panel) return undefined;
    const timer = setTimeout(() => {
      accordionRefs.current[panel]?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
      setSidebar((pre) => ({ ...pre, scrollTo: null }));
    }, 300);
    return () => clearTimeout(timer);
  }, [sidebar.scrollTo, setSidebar]);

  const addFamily = React.useMemo(() => {
    return (
      <AddRelativePanel
        setPedigreeState={setPedigreeState}
        node={data}
        user={selectedUser}
        setMemberRecords={setMemberRecords}
        memberRecords={memberRecords}
        // Adds stay disabled for the WHOLE save round trip (fire →
        // response-merge): a click landing mid-flight used to get its
        // structural edits reverted by the response reconciliation.
        isDisabled={sectionDisabled || isSaving}
      />
    );
  }, [data, memberRecords, sectionDisabled, isSaving, selectedUser]);

  const getAccordianDetails = (value) => {
    switch (value) {
      case SIDEBAR_VALUE.person:
        return (
          <PersonMenu
            setPedigreeState={setPedigreeState}
            data={data}
            setMemberRecords={setMemberRecords}
            memberRecords={memberRecords}
            onDirtyChange={(d) =>
              d ? markDirty('person') : markClean('person')
            }
            saveRef={personSaveRef}
            discardRef={personDiscardRef}
            isDisabled={sectionDisabled}
            modificationHistoryHandle={modificationHistoryHandle}
            onProbandEmailSaved={() => setFamilyHistoryRefreshKey((k) => k + 1)}
            onSelectMember={(pid) =>
              setSidebar((pre) => ({ ...pre, selectPid: pid }))
            }
            // The container opts in, and only the person tab does. The drawer
            // keeps one field per row at both its widths (TGCLOUD-1669).
            //
            // THIS PROP WAS ON ReproductiveSection, which does not read it, so
            // the whole field-packing change was inert: PersonMenu always took
            // its `wideLayout = false` default and stayed one column under a
            // 600px cap. Nothing caught it, because every test of the mechanism
            // called formGridSx directly and the mechanism was never wrong. It
            // was simply never switched on.
            wideLayout={isRegion}
          />
        );
      case SIDEBAR_VALUE.tags:
        // Person view (a node is selected) → person-level tags; family view
        // (no node) → the existing family-level tags. Both reuse the tag refs.
        return data ? (
          <PersonTags
            data={data}
            user={selectedUser}
            onDirtyChange={(d) => (d ? markDirty('tags') : markClean('tags'))}
            saveRef={tagsSaveRef}
            discardRef={tagsDiscardRef}
          />
        ) : (
          <Tags
            user={selectedUser}
            onDirtyChange={(d) => (d ? markDirty('tags') : markClean('tags'))}
            saveRef={tagsSaveRef}
            discardRef={tagsDiscardRef}
          />
        );
      case SIDEBAR_VALUE.externalFamilyIds:
        return (
          <ExternalFamilyIds
            user={selectedUser}
            onDirtyChange={(d) =>
              d
                ? markDirty('externalFamilyIds')
                : markClean('externalFamilyIds')
            }
            onSaved={handleExternalIdsSaved}
            saveRef={externalIdsSaveRef}
            discardRef={externalIdsDiscardRef}
          />
        );
      case SIDEBAR_VALUE.familyNote:
        return (
          <FamilyNote
            user={selectedUser}
            onDirtyChange={(d) =>
              d ? markDirty('familyNote') : markClean('familyNote')
            }
            saveRef={familyNoteSaveRef}
            discardRef={familyNoteDiscardRef}
            onSaved={handleFamilyNoteSaved}
          />
        );
      case SIDEBAR_VALUE.familyHistory:
        return (
          <FamilyHistoryMenu
            user={selectedUser}
            active={
              sidebar.expandAccordian?.includes(SIDEBAR_VALUE.familyHistory) ||
              false
            }
            refreshKey={familyHistoryRefreshKey}
          />
        );
      case SIDEBAR_VALUE.addRelative:
        return addFamily;
      case SIDEBAR_VALUE.riskFactors:
        return (
          <RiskFactorsMenu
            reportSummary={reportSectionFacts}
            data={data}
            modificationHistoryHandle={modificationHistoryHandle}
          />
        );
      case SIDEBAR_VALUE.export:
        return <ExportMenu data={data} user={selectedUser} />;
      case SIDEBAR_VALUE.phenotypes:
        return (
          <PhenotypesMenu
            handleClose={handleClose}
            data={data}
            setMemberRecords={setMemberRecords}
            memberRecords={memberRecords}
            modificationHistoryHandle={modificationHistoryHandle}
            setPedigreeState={setPedigreeState}
            setLabelsSet={setLabelsSet}
            onDirtyChange={(d) =>
              d ? markDirty('phenotypes') : markClean('phenotypes')
            }
            saveRef={phenotypesSaveRef}
            discardRef={phenotypesDiscardRef}
            isDisabled={sectionDisabled}
          />
        );
      case SIDEBAR_VALUE.genotypes:
        return (
          <GenotypesMenu
            handleClose={handleClose}
            data={data}
            setMemberRecords={setMemberRecords}
            memberRecords={memberRecords}
            modificationHistoryHandle={modificationHistoryHandle}
            setPedigreeState={setPedigreeState}
            setLabelsSet={setLabelsSet}
            onDirtyChange={(d) =>
              d ? markDirty('genotypes') : markClean('genotypes')
            }
            saveRef={genotypesSaveRef}
            discardRef={genotypesDiscardRef}
            isDisabled={sectionDisabled}
          />
        );
      case SIDEBAR_VALUE.diagnoses:
        return (
          <DiagnosesMenu
            handleClose={handleClose}
            data={data}
            setMemberRecords={setMemberRecords}
            memberRecords={memberRecords}
            modificationHistoryHandle={modificationHistoryHandle}
            setPedigreeState={setPedigreeState}
            setLabelsSet={setLabelsSet}
            onDirtyChange={(d) =>
              d ? markDirty('diagnoses') : markClean('diagnoses')
            }
            saveRef={diagnosesSaveRef}
            discardRef={diagnosesDiscardRef}
            isDisabled={sectionDisabled}
          />
        );
      case SIDEBAR_VALUE.suggestions:
        return (
          <Suggestions
            reportSummary={reportSectionFacts}
            // Mirrors FamilyHistoryMenu above: the section only does its
            // expensive work once someone opens it. Sections are ALWAYS
            // mounted (SidebarSection has no unmountOnExit), and a person tab
            // seeds its selection on mount, so without this every person-tab
            // open paid the ranking cost before the clinician had asked for it.
            active={
              sidebar.expandAccordian?.includes(SIDEBAR_VALUE.suggestions) ||
              false
            }
            data={data}
            setDataset={setMemberRecords}
            analysis={analysis}
          />
        );
      case SIDEBAR_VALUE.pedigreeMatch:
        return (
          <PedigreeMatchMenu
            analysis={analysis}
            data={data}
            memberRecords={memberRecords}
          />
        );
      case SIDEBAR_VALUE.emr:
        return <EpicEmrButtonWrapper data={data} emrSettings={epicEmrApi} />;
      case SIDEBAR_VALUE.clinicalNotes:
        return (
          <ClinicalNotes
            reportSummary={reportSectionFacts}
            openBrowserSignal={browserSignal}
            dockNode={promoted && !recordsCollapsed ? dockNode : null}
            data={data}
            memberRecords={memberRecords}
            setMemberRecords={setMemberRecords}
            modificationHistoryHandle={modificationHistoryHandle}
            // The NARROWER gate — clinical records have their own endpoints and
            // are not blocked by another user's family lock.
            isDisabled={clinicalRecordsDisabled}
            addFetusEvent={sidebar?.addFetusEvent}
            onAddFetusEventHandled={() =>
              setSidebar((pre) => ({ ...pre, addFetusEvent: null }))
            }
          />
        );
      case SIDEBAR_VALUE.reproductive:
        return (
          <ReproductiveSection
            data={data}
            openRecordId={sidebar?.openReproRecordId}
            onSelectMember={(pid) =>
              setSidebar((pre) => ({ ...pre, selectPid: pid }))
            }
            onAddFetusEvent={(scope) =>
              setSidebar((pre) => ({
                ...pre,
                open: true,
                // Switch the clinician to Clinical Records, where the record form lives, and carry
                // the fetus scope with them.
                expandAccordian: [SIDEBAR_VALUE.clinicalNotes],
                addFetusEvent: scope
              }))
            }
            onOpenRecordHandled={() =>
              setSidebar((pre) => ({ ...pre, openReproRecordId: null }))
            }
            user={selectedUser}
            memberRecords={memberRecords}
            setMemberRecords={setMemberRecords}
            setPedigreeState={setPedigreeState}
            modificationHistoryHandle={modificationHistoryHandle}
            isDisabled={sectionDisabled || isSaving}
          />
        );
      case SIDEBAR_VALUE.formSubmission:
        return (
          <FormSubmission data={data} reportSummary={reportSectionFacts} />
        );
      case SIDEBAR_VALUE.pathways:
        return <Pathways data={data} reportSummary={reportSectionFacts} />;
      case SIDEBAR_VALUE.recordStatus:
        return (
          <RecordStatusMenu
            data={data}
            refreshKey={recordStatusRefreshKey}
            onLinkStatusChanged={onLinkStatusChanged}
          />
        );
      default:
        return <div>New Feature Coming Soon...</div>;
    }
  };

  const getSideBarItems = () => {
    const baseItems = isStudent
      ? STUDENT_SIDEBAR_ITEMS
      : data
      ? getUserSidebarItems({
          showEmrTab: epicEmrApi?.enabled,
          // REPRODUCTIVE is hidden for an assigned-male individual (TGCLOUD-1617 item 6). Read the
          // member from memberRecords where possible — `data` can predate hydration, and the
          // pregnancies list is what keeps an already-recorded pregnancy reachable.
          member: (memberRecords || []).find((m) => m.pid === data?.pid) || data
        })
      : SIDEBAR_ITEMS;

    const canExport = !!loggedInUser?.userGroup?.canExport;

    const sidebarItems = baseItems.filter((item) => {
      if (!canExport && item.value === SIDEBAR_VALUE.export) return false;
      // Add Relative requires the pedigree lock — hide it for other users
      if (pedigreeLockedByOther && item.value === SIDEBAR_VALUE.addRelative)
        return false;
      // Clinical Insights gated by the admin's Virtual Assistant toggle.
      // `featureFlags` is null on first paint — keep the item visible until
      // we know, to avoid a brief disappearing accordion when flags load.
      // Pedigree Match shares this gate — part of the same
      // analysis/clinical-insights prototype, hidden when Clinical Insights is
      // disabled.
      if (
        (item.value === SIDEBAR_VALUE.suggestions ||
          item.value === SIDEBAR_VALUE.pedigreeMatch) &&
        featureFlags &&
        !featureFlags.clinicalInsightsEnabled
      ) {
        return false;
      }
      // Family History follows the Family History Questionnaire platform
      // feature — on by default, hidden only when explicitly disabled.
      if (
        item.value === SIDEBAR_VALUE.familyHistory &&
        systemFeatures?.FEATURE_FAMILY_HISTORY_QUESTIONNAIRE === false
      ) {
        return false;
      }
      // Pathways follows the Pathways system feature — on by default, hidden
      // only when an admin has explicitly disabled it in settings.
      if (
        item.value === SIDEBAR_VALUE.pathways &&
        systemFeatures?.FEATURE_PATHWAYS === false
      ) {
        return false;
      }
      // Anonymous pedigrees: external family IDs are removed by decision,
      // EMR/SMART is incompatible, and form submission / patient access
      // need a patient email that cannot exist. Backend rejects all four.
      if (
        anonymousActive &&
        [
          SIDEBAR_VALUE.externalFamilyIds,
          SIDEBAR_VALUE.emr,
          SIDEBAR_VALUE.formSubmission,
          SIDEBAR_VALUE.setupPatientAccess
        ].includes(item.value)
      ) {
        return false;
      }
      return true;
    });

    return sidebarItems;
  };

  const getExpandedValue = (val) => {
    return sidebar.expandAccordian?.includes(val) ? true : false;
  };

  // TGCLOUD-1669 Phase 2 — the pinned person header. Built from the SAME merged
  // member the summaries use, so the header cannot disagree with the rows under
  // it, and from the shared tenant label map so a renamed field is renamed here
  // too. `buildPersonHeader` delegates the name/id fallbacks to
  // `resolveSidebarHeader`, including the TGCLOUD-1768 fail-closed rule.
  const fieldLabels = usePedigreeFieldLabels();
  const header = useMemo(
    () =>
      buildPersonHeader({
        data: summaryMember,
        selectedUser,
        anonymous: anonymousOrLoading,
        labels: fieldLabels,
        personLinkStatus
      }),
    [
      summaryMember,
      selectedUser,
      anonymousOrLoading,
      fieldLabels,
      personLinkStatus
    ]
  );

  // Pulled out of the layouts below so the peek and the promoted view render
  // the SAME banners and the SAME section stack. Two copies would drift, and
  // the one that drifted would be the one nobody was looking at.
  const banners = (
    <>
      {/* Lock banner — shown when this person is edited by another user */}
      {lockedByOther && (
        <SidebarLockBanner
          lockInfo={lockInfo}
          requestStatus={personLockHook.getRequestStatus(data?.pid)}
          declineMessage={personLockHook.getDeclineMessage(data?.pid)}
          onRequest={() => personLockHook.requestLock(data.pid)}
          onCancelRequest={() => personLockHook.cancelRequestLock(data.pid)}
          onForceAcquire={() => personLockHook.forceAcquireLock(data.pid)}
        />
      )}

      {/* Bulk edit active banner */}
      {bulkEditActive && !lockedByOther && (
        <Box
          sx={{
            mx: 1,
            my: 1,
            px: 2,
            py: 1.5,
            bgcolor: 'grey.100',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.300'
          }}
        >
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Sidebar editing is unavailable while a bulk edit session is in
            progress.
          </Typography>
        </Box>
      )}

      {isSecondaryRecord && !lockedByOther && !bulkEditActive && (
        <Alert severity="warning" sx={{ flexShrink: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Secondary Record Editing Locked
          </Typography>
          <Typography variant="caption">
            Primary:{' '}
            <strong>{personLinkData?.primaryFamilyMemberId || '—'}</strong>
          </Typography>
        </Alert>
      )}

      {/* The state that used to have nothing: family locked by a colleague,
          no person lock held, every section disabled and unexplained. The
          exclusions that keep this from competing with the three banners above
          are in takeLockIntent. */}
      {offerTakeLock && (
        <TakeEditingLockBanner busy={takingLock} onTake={handleTakeLock} />
      )}
    </>
  );

  const sectionStack = (
    <ListContainer>
      {getSideBarItems().map((item) => (
        <SidebarSection
          key={item.value}
          item={item}
          expanded={getExpandedValue(item.value)}
          onChange={handleChangeAccordian(item.value)}
          summaryRef={(el) => (accordionRefs.current[item.value] = el)}
          // undefined for any row we have nothing to say about yet, which
          // renders nothing rather than claiming "none recorded".
          summary={sectionSummaries[item.value]}
          dirty={
            [
              SIDEBAR_VALUE.person,
              SIDEBAR_VALUE.diagnoses,
              SIDEBAR_VALUE.phenotypes,
              SIDEBAR_VALUE.genotypes
            ].includes(item.value) && isDirty(item.value)
          }
        >
          {getAccordianDetails(item.value)}
        </SidebarSection>
      ))}
    </ListContainer>
  );

  const headerBand = (
    <PersonHeaderBand
      header={header}
      onClose={isRegion ? undefined : toggleSideBar(false)}
      headerColour={theme.header}
      maxFacts={promoted ? 8 : 5}
      actions={
        <>
          <HeaderActionButton
            icon={<ManageSearchIcon sx={{ fontSize: 16 }} />}
            onClick={() => setBrowserSignal((n) => n + 1)}
            count={sectionFacts[SIDEBAR_VALUE.clinicalNotes]?.browserCount}
          >
            Clinical records
          </HeaderActionButton>
          <HeaderActionButton
            icon={<HistoryIcon sx={{ fontSize: 16 }} />}
            onClick={() =>
              modificationHistoryHandle(CONSTANTS.auditLogType.personAll)
            }
          >
            Change history
          </HeaderActionButton>
          {/* The way a person tab gets opened (TGCLOUD-1669 Phase 7). Offered
              from the drawer only: in a person tab you are already there, and
              in a single-chart view there is no strip to open into.
              `_id` is the identity, not the pid: a pid is a per-family small
              integer and pid 1 is the proband of every family. */}
          {/* The way back. Without it a person tab is a dead end: the record
              names a family it cannot take you to. Offered from the region
              only, since the drawer is already on the chart. */}
          {isRegion && workspace?.openPedigree && (
            <HeaderActionButton
              icon={<AccountTreeOutlinedIcon sx={{ fontSize: 15 }} />}
              onClick={() =>
                workspace.openPedigree(selectedUser?.familyId || data?.familyId)
              }
            >
              Open pedigree
            </HeaderActionButton>
          )}
          {!isRegion && workspace?.openPerson && data?._id && (
            <HeaderActionButton
              icon={<OpenInNewOutlinedIcon sx={{ fontSize: 15 }} />}
              onClick={() =>
                workspace.openPerson(String(data._id), {
                  familyId: selectedUser?.familyId || data?.familyId,
                  title: header?.primary || data?.familyMemberId || 'Patient',
                  pinned: true
                })
              }
            >
              Open in tab
            </HeaderActionButton>
          )}
          {/* One control, same place in both states, so trying it costs
              nothing: the open sections, the dirty state, the lock and any
              unsaved edits all survive the round trip, because the section
              stack keeps its position in the tree and is never remounted.
              captureScrollAnchor above then puts the section you were reading
              back where it was, which a raw scrollTop cannot do across a
              reflow. */}
          {!isRegion && (
            <HeaderActionButton
              icon={
                promoted ? (
                  <CloseFullscreenIcon sx={{ fontSize: 15 }} />
                ) : (
                  <OpenInFullIcon sx={{ fontSize: 15 }} />
                )
              }
              onClick={() => {
                captureScrollAnchor();
                setPromoted((v) => !v);
              }}
            >
              {promoted ? 'Collapse' : 'Promote'}
            </HeaderActionButton>
          )}
        </>
      }
    />
  );

  const saveFooter = !sectionDisabled && (
    <SaveFooter
      dirtyCount={dirtyCount}
      dirtySectionLabels={dirtySectionLabels}
      onSave={handleSaveAll}
      onDiscard={handleDiscardAll}
    />
  );

  const list = (anchor) => (
    <StyledList
      role="presentation"
      promoted={promoted}
      // `full` means "take the width you are given" rather than a fixed 400 or
      // the promoted cap. A region always does.
      full={anchor === 'region' || anchor === 'top' || anchor === 'bottom'}
    >
      {/* ONE tree, not two. The promoted-only columns are conditional SIBLINGS
          of the scrolling content rather than a branch wrapped around it,
          because React reconciles by position: rendering sectionStack inside
          two different branches unmounts and remounts every section on each
          promote and demote. PersonMenu holds its formValues in local state,
          so that quietly discarded a clinician's unsaved edits while the
          parent's dirty flag stayed true, leaving a save footer offering to
          save values that were already gone. */}
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* 1. identity, then the pinned order as navigation */}
        {promoted && (
          <Box
            sx={{
              width: isRegion ? 236 : 268,
              flexShrink: 0,
              borderRight: '1px solid #dde3e9',
              bgcolor: '#fbfcfd',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0
            }}
          >
            {headerBand}
            <SectionIndex
              items={getSideBarItems()}
              counts={sectionCounts}
              activeKey={activeSection}
              onSelect={handleIndexSelect}
            />
          </Box>
        )}

        {/* 2. the sections themselves, in the SAME position in both states.
            Promoting gives them more room; it does not re-lay them out. */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            // CAPPED in the region, and that is the point. Left uncapped the
            // centre absorbs every extra pixel, which is how a 1340px column
            // ended up holding one field per row while the record column sat
            // cramped at 348px. 760px is two 370px tracks at the 280px floor,
            // which is what puts first and last name beside each other, and
            // everything past it goes to the records instead.
            ...(isRegion ? { maxWidth: 760 } : null)
          }}
        >
          {!promoted && headerBand}
          {banners}
          <ScrollableContent ref={sidebarRef}>{sectionStack}</ScrollableContent>
        </Box>

        {/* 3. the record browser, docked. Dropped first as the window
              narrows: below roughly 1240px of viewport the drawer is under
              980px, and a 348px column out of that leaves the sections too
              narrow to be worth promoting for. The header control still opens
              the browser as a dialog, so the records stay one click away. */}
        {promoted && !recordsCollapsed && (
          <Box
            sx={{
              // The drawer keeps its fixed 348. In the region the records take
              // everything the capped centre does not, so they end up WIDER
              // than the centre on a large screen: the tile titles truncate by
              // ellipsis, so width is what lets a clinician read one without
              // opening it.
              ...(isRegion
                ? { flex: 1, minWidth: 380 }
                : { width: 348, flexShrink: 0 }),
              borderLeft: '1px solid #dde3e9',
              bgcolor: '#fbfcfd',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              '@media (max-width: 1239px)': { display: 'none' }
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 1,
                borderBottom: '1px solid #dde3e9',
                bgcolor: 'background.paper'
              }}
            >
              <Typography
                sx={{
                  flex: 1,
                  fontSize: 10.5,
                  fontWeight: 800,
                  letterSpacing: '.7px',
                  textTransform: 'uppercase',
                  color: '#2b3648'
                }}
              >
                Clinical records
              </Typography>
              {!isRegion && (
                <IconButton
                  size="small"
                  aria-label="Collapse the record column"
                  onClick={() => {
                    captureScrollAnchor();
                    setDockCollapsed(true);
                  }}
                >
                  <KeyboardTabIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Box>
            {/* ClinicalNotes portals the browser in here. */}
            <Box
              ref={setDockNode}
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column'
              }}
            />
          </Box>
        )}

        {promoted && recordsCollapsed && (
          <Box
            sx={{
              width: 36,
              flexShrink: 0,
              borderLeft: '1px solid #dde3e9',
              bgcolor: '#fbfcfd',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              pt: 1,
              '@media (max-width: 1239px)': { display: 'none' }
            }}
          >
            <IconButton
              size="small"
              aria-label="Show the record column"
              onClick={() => {
                captureScrollAnchor();
                setDockCollapsed(false);
              }}
            >
              <ManageSearchIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Box>
        )}
      </Box>

      {saveFooter}
    </StyledList>
  );

  return (
    <div>
      <BtnContainer style={{ display: 'none' }}>
        <IconButton onClick={toggleSideBar(true)} size="large">
          <MenuIcon />
        </IconButton>
      </BtnContainer>
      {openModal && (
        <CustomDialog
          title={
            modificationRecordId
              ? 'Record History'
              : modalTitleMap[modificationType]
              ? modalTitleMap[modificationType]
              : modificationType
          }
          open={openModal}
          handleClose={handleCloseModel}
          minHeight="auto"
          maxWidth="lg"
          fullWidth
        >
          <ModificationHistoryModal
            open={openModal}
            modificationType={modificationType}
            modificationSubType={modificationSubType}
            recordId={modificationRecordId}
            handleClose={handleCloseModel}
            familyMemberId={familyMemberId}
          />
        </CustomDialog>
      )}
      {/* The shell, and the ONLY thing that differs between the two. A person
          tab has no canvas to float over, so there is nothing for a Drawer to
          be persistent against: the same content simply fills the region. */}
      {isRegion ? (
        <Box sx={REGION_SHELL_SX}>
          {/* Opening a patient tab changes nothing a screen reader notices.
              The tab strip is at the other end of the document, the header
              band is a soft update inside an already-rendered region, and the
              spinner below unmounts when the record arrives - and a live
              region that DISAPPEARS announces nothing, so putting role=status
              on the spinner alone would report the wait and never the answer.

              One region that persists across both states instead, so the two
              transitions a clinician needs are the two it actually makes: the
              wait, and then which patient they are now looking at. Polite, so
              it queues behind anything the clinician is reading rather than
              cutting across it. */}
          <Box
            role="status"
            aria-live="polite"
            sx={{
              position: 'absolute',
              width: 1,
              height: 1,
              overflow: 'hidden',
              clip: 'rect(0 0 0 0)',
              whiteSpace: 'nowrap'
            }}
          >
            {personPending
              ? 'Loading patient record'
              : header?.primary
              ? `Patient record loaded: ${header.primary}`
              : ''}
          </Box>
          {/* A person tab must never show the FAMILY view, not even for the
              beat before its member resolves. `data` is null until the family
              arrives and the seeding effect finds the member, and a null `data`
              means "family" to getSideBarItems, so the tab painted eight family
              sections and then swapped to sixteen person ones. Rendering the
              person list early would be no better: every section would report
              "none recorded" for a person whose records simply have not
              arrived, which is a clinical claim rather than a loading state. */}
          {personPending ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                color: 'text.secondary',
                fontSize: 13
              }}
            >
              <CircularProgress size={18} thickness={5} />
              Loading patient record...
            </Box>
          ) : (
            list('region')
          )}
        </Box>
      ) : (
        <Drawer
          variant="persistent"
          anchor="right"
          open={sidebar.open}
          onClose={toggleSideBar(false)}
          sx={{
            zIndex: 2100,
            '& .MuiDrawer-paper': {
              top: '65px',
              bottom: 0,
              height: 'auto'
            }
          }}
          slotProps={{
            backdrop: { invisible: true }
          }}
        >
          {list('right')}
        </Drawer>
      )}
    </div>
  );
}
